/* eslint-disable camelcase,dot-notation */
import { createActions } from 'redux-actions';
import map from 'lodash/map';
import { addFlashMessage, clearMessage } from '../Flash';
import {
  mapFreelancerSkillsToDescription,
  mapFreelancerSkillsToDescriptionWithCategory,
  sendUpload,
  getErrorMessages,
} from '../../../shared/helpers';
import api from '../../../api';

export const {
  selectStepValue,
  selectStepInputValue,
  setNextStep,
  setCurrentOnboardingStep,
  setCurrentSection,
  updateProjectBrief,
  uploadProjectBriefAttachment,
  uploadProjectPicture,
  removeProjectBriefAttachment,
  removeProjectPicture,
  addDropboxPath,
  removeDropboxFolder,
  addReference,
  deleteReference,
  updateReference,
  updateFreeComment,
  setPrevStep,
  setBudget,
  getBudgetSuccess,
  fetchingFreelancersSuccess,
  setDeadline,
  changeManagementDescription,
  updateFreelancerPreFilter,
  requestNextStep,
  uploadingAttachmentRequest,
  uploadingProjectPictureRequest,
  getBudgetRequest,
  getBudgetFailure,
  fetchingFreelancersRequest,
  fetchingFreelancersFailure,
  requestBidRequest,
  requestBidSuccess,
  requestBidFailure,
  deleteBidRequest,
  deleteBidSuccess,
  deleteBidFailure,
  bookNowRequest,
  bookNowSuccess,
  bookNowFailure,
} = createActions(
  {
    SELECT_STEP_VALUE: (data, overviewStep, selectedValue) => ({
      data,
      overviewStep,
      selectedValue,
    }),
    SELECT_STEP_INPUT_VALUE: (
      data,
      overviewStep,
      selectedValue,
      inputValue,
    ) => ({ data, overviewStep, selectedValue, inputValue }),
    SET_NEXT_STEP: overviewStep => ({ overviewStep }),
    SET_CURRENT_ONBOARDING_STEP: currentStep => ({ currentStep }),
    SET_CURRENT_SECTION: (overviewStep, onboardingStep, sectionToGo) => ({
      overviewStep,
      onboardingStep,
      sectionToGo,
    }),
    UPDATE_PROJECT_BRIEF: (
      overviewStep,
      onboardingStep,
      inputField,
      inputValue,
    ) => ({ overviewStep, onboardingStep, inputField, inputValue }),
    UPLOAD_PROJECT_BRIEF_ATTACHMENT: (overviewStep, onboardingStep, file) => ({
      overviewStep,
      onboardingStep,
      file,
    }),
    UPLOAD_PROJECT_PICTURE: (overviewStep, onboardingStep, file) => ({
      overviewStep,
      onboardingStep,
      file,
    }),
    REMOVE_PROJECT_BRIEF_ATTACHMENT: (
      overviewStep,
      onboardingStep,
      attachment,
    ) => ({ overviewStep, onboardingStep, attachment }),
    REMOVE_PROJECT_PICTURE: (overviewStep, onboardingStep) => ({
      overviewStep,
      onboardingStep,
    }),
    ADD_DROPBOX_PATH: (overviewStep, onboardingStep, links) => ({
      overviewStep,
      onboardingStep,
      links,
    }),
    REMOVE_DROPBOX_FOLDER: (overviewStep, onboardingStep) => ({
      overviewStep,
      onboardingStep,
    }),
    ADD_REFERENCE: (overviewStep, onboardingStep, newElement) => ({
      overviewStep,
      onboardingStep,
      newElement,
    }),
    DELETE_REFERENCE: (overviewStep, onboardingStep, reference) => ({
      overviewStep,
      onboardingStep,
      reference,
    }),
    UPDATE_REFERENCE: (
      overviewStep,
      onboardingStep,
      inputField,
      inputValue,
      index,
    ) => ({ overviewStep, onboardingStep, inputField, inputValue, index }),
    UPDATE_FREE_COMMENT: (overviewStep, onboardingStep, inputValue) => ({
      overviewStep,
      onboardingStep,
      inputValue,
    }),
    SET_PREV_STEP: overviewStep => ({ overviewStep }),
    SET_BUDGET: data => ({ data }),
    GET_BUDGET_SUCCESS: data => ({ data }),
    FETCHING_FREELANCERS_SUCCESS: data => ({ data }),
    SET_DEADLINE: (overviewStep, onboardingStep, deadline) => ({
      overviewStep,
      onboardingStep,
      deadline,
    }),
    CHANGE_MANAGEMENT_DESCRIPTION: managementWay => ({ managementWay }),
    UPDATE_FREELANCER_PRE_FILTER: data => ({ data }),
  },

  'REQUEST_NEXT_STEP',
  'UPLOADING_ATTACHMENT_REQUEST',
  'UPLOADING_PROJECT_PICTURE_REQUEST',
  'GET_BUDGET_REQUEST',
  'GET_BUDGET_FAILURE',
  'FETCHING_FREELANCERS_REQUEST',
  'FETCHING_FREELANCERS_FAILURE',
  'REQUEST_BID_REQUEST',
  'REQUEST_BID_SUCCESS',
  'REQUEST_BID_FAILURE',
  'DELETE_BID_REQUEST',
  'DELETE_BID_SUCCESS',
  'DELETE_BID_FAILURE',
  'BOOK_NOW_REQUEST',
  'BOOK_NOW_SUCCESS',
  'BOOK_NOW_FAILURE',
);

export const goToStep = overviewStep => dispatch => {
  dispatch(requestNextStep());
  dispatch(setNextStep(overviewStep));
};

export const uploadFile = (
  overviewStep,
  onboardingStep,
  file,
  fileOrigin,
  updateFormInfo,
  formInfo,
) => dispatch => {
  switch (fileOrigin) {
    case 'attachment':
      dispatch(
        attachmentUpload(
          overviewStep,
          onboardingStep,
          file,
          updateFormInfo,
          formInfo,
        ),
      );
      break;
    case 'project_picture':
      dispatch(projectPictureUpload(overviewStep, onboardingStep, file));
      break;
    default:
      dispatch(addFlashMessage(['Not recognized file origin'], 'error'));
  }
};

const attachmentUpload = (
  overviewStep,
  onboardingStep,
  file,
  updateFormInfo,
  formInfo,
) => dispatch => {
  const { name } = file;
  sendUpload(file)
    .then(url => {
      dispatch(
        uploadProjectBriefAttachment(overviewStep, onboardingStep, {
          name,
          url,
        }),
      );
      const formAnswers = formInfo.answered_questions;
      updateFormInfo({
        target: {
          name: 'answered_questions',
          value: {
            ...formInfo.answered_questions,
            files: [{name, url}].concat(formAnswers.files),
          },
        },
      });
    })
    .catch(() => {
      dispatch(addFlashMessage(`${name} has not been accepted.`, 'error'));
    });
};

export const deleteReferenceVideo = (
  overviewStep,
  onboardingStep,
  reference,
) => dispatch => {
  if (reference.id) {
    api
      .deleteReference(reference.id)
      .then(() => {
        dispatch(deleteReference(overviewStep, onboardingStep, reference));
      })
      .catch(e => {
        const errors = getErrorMessages(e);
        return dispatch(addFlashMessage(errors, 'error'));
      });
  } else {
    dispatch(deleteReference(overviewStep, onboardingStep, reference));
  }
};

const projectPictureUpload = (
  overviewStep,
  onboardingStep,
  file,
) => dispatch => {
  const { name } = file;
  sendUpload(file)
    .then(url => {
      dispatch(
        uploadProjectPicture(overviewStep, onboardingStep, {
          name,
          url,
        }),
      );
    })
    .catch(() => {
      dispatch(addFlashMessage(`${name} has not been accepted.`, 'error'));
    });
};

export const removeUpload = (
  overviewStep,
  onboardingStep,
  file,
  fileOrigin,
) => dispatch => {
  if (fileOrigin === 'attachment') {
    const dispatching = dispatch(
      removeProjectBriefAttachment(overviewStep, onboardingStep, file),
    );
    dispatch(addFlashMessage([`File remove success message`], 'success'));
    return dispatching;
  } else if (fileOrigin === 'project_picture') {
    return api
      .deleteFile(file)
      .then(() => dispatch(removeProjectPicture(overviewStep, onboardingStep)))
      .catch(() => {
        dispatch(addFlashMessage([`File remove error message`], 'error'));
      });
  }
  return dispatch(addFlashMessage(['Not recognized file origin'], 'error'));
};

export const addDropboxFolder = (
  overviewStep,
  onboardingStep,
  dropboxPath,
) => dispatch => {
  dispatch(
    addDropboxPath(
      overviewStep,
      onboardingStep,
      dropboxPath.map(resource => resource.link),
    ),
  );
};

export const notAllowedFileType = path => dispatch => {
  dispatch(
    addFlashMessage(
      [`${path.name} selected in dropbox is not a folder`],
      'error',
    ),
  );
};

export const getBudget = projectId => dispatch => {
  dispatch(getBudgetRequest());
  return api
    .getBudget(projectId)
    .then(response => response.data)
    .then(json => {
      if (json.errors) return dispatch(addFlashMessage(json.errors, 'error'));
      return dispatch(getBudgetSuccess(json));
    })
    .catch(() => {
      dispatch(clearMessage());
      dispatch(
        addFlashMessage(['Something went wrong, try again later'], 'error'),
      );
      return dispatch(getBudgetFailure());
    });
};

export const fetchFreelancers = (params = {}) => (dispatch, getState) => {
  dispatch(fetchingFreelancersRequest());
  const { preFilter } = getState().onboarding.freelancersPage;
  const mergedParams = {
    ...preFilter,
    ...params,
  };

  return api
    .fetchFreelancers(mergedParams)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(fetchingFreelancersFailure());
      }
      const freelancersWithFormattedSkills = map(
        json.freelancers,
        freelancer => ({
          ...freelancer,
          formatted_skills: mapFreelancerSkillsToDescription(freelancer.skills),
          ...mapFreelancerSkillsToDescriptionWithCategory(freelancer.skills),
        }),
      );
      return dispatch(
        fetchingFreelancersSuccess({
          freelancers: freelancersWithFormattedSkills,
          pagination: json.pagination,
        }),
      );
    })
    .catch(() => {
      dispatch(
        addFlashMessage(
          ['Something went wrong when fetching freelancers, try again later'],
          'error',
        ),
      );

      return dispatch(fetchingFreelancersFailure());
    });
};

export const requestBid = (projectId, freelancerId) => dispatch => {
  dispatch(requestBidRequest());

  return api
    .requestBid(projectId, freelancerId)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(requestBidFailure());
      }

      return dispatch(requestBidSuccess());
    })
    .catch(() => dispatch(requestBidFailure()));
};

export const deleteBid = (projectId, bidId) => dispatch => {
  dispatch(deleteBidRequest());

  return api
    .deleteBid(projectId, bidId)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(deleteBidFailure());
      }

      return dispatch(deleteBidSuccess());
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(deleteBidFailure());
    });
};

export const bookNow = (projectId, freelancerId) => dispatch => {
  dispatch(bookNowRequest());
  return api
    .bookNow(projectId, freelancerId)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(bookNowFailure());
      }

      return dispatch(bookNowSuccess());
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(bookNowFailure());
    });
};



// WEBPACK FOOTER //
// ./src/store/actions/Onboarding/index.js